import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment from 'moment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { IdName } from '../models/basic/idname.model';
import { TokenResult } from '../models/basic/tokenresult.model';
import { User } from '../models/basic/user.model';
import { LoginError } from '../models/enums/loginerror.enum';
import { RequestBase } from './request-base';

@Injectable()
export class UsersService extends RequestBase {

    static Login: Subject<any> = new Subject();
    static Logout: Subject<any> = new Subject();
    static TokenSet: Subject<void> = new Subject();
    static BeforeLogout: Subject<any> = new Subject();
    static ActualUserRights: BehaviorSubject<any> = new BehaviorSubject(null);
    static ActiveUser: BehaviorSubject<any> = new BehaviorSubject(null);

    BasePath = 'config/api/users';

    /// Loescht nur die Anmeldedaten, ohne Ereignisse auszuloesen. Bewusst getrennt von
    /// LogoutStatic: dort wird vorher BeforeLogout ausgeloest, und wenn ein Abonnent
    /// dabei scheitert -- etwa der WebSocket beim Senden --, kaeme das Loeschen nie dran.
    static ClearCredentials() {
        localStorage.removeItem('user');
        localStorage.removeItem('credentials');
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('expires');
        localStorage.removeItem('expiresat');
        localStorage.removeItem('userid');
    }

    static LogoutStatic() {
        UsersService.BeforeLogout.next(null);
        UsersService.ClearCredentials();
        UsersService.Logout.next(null);
        UsersService.ActualUserRights.next(null);
        UsersService.ActiveUser.next(null);
    }

    static SetToken(res) {
        localStorage.setItem('token', res.access_token);
        localStorage.setItem('expires', res.expires + '');
        localStorage.setItem('expiresat', moment().add(moment.duration(res.expires, 'seconds')).toJSON());
        // Persist the rotated refresh_token too — without this the browser keeps using the
        // initial login refresh_token on every subsequent refresh. Eventually GACentral's
        // replay-detection fires (UsedAt > 30s grace window) and invalidates every session
        // of the user → "Invalid or expired refresh token" until the user logs in again.
        if (res.refresh_token) {
            localStorage.setItem('refresh_token', res.refresh_token);
        }
        UsersService.TokenSet.next();
    }

    constructor(http: HttpClient) {
        super(http);
    }

    ReadUserInfos(): Observable<IdName[]> {
        return this.http.get<IdName[]>(this.API_BASE_URL + this.BasePath + '/ReadUserInfos', this.options);
    }
    readUsers(): Observable<User[]> {
        return this.http.get<User[]>(this.API_BASE_URL + this.BasePath + '/readusers', this.options);
    }
    findUser(id: string): Observable<User> {
        return this.http.get<User>(this.API_BASE_URL + this.BasePath + '/finduser?id=' + id, this.options);
    }
    createUser(user: User) {
        return this.http.post(this.API_BASE_URL + this.BasePath + '/AddOrUpdateUser', user, this.options).pipe(
            catchError(this.handleError('createUser', []))
        );
    }
    deleteUser(id: string) {
        return this.http.delete(this.API_BASE_URL + this.BasePath + '/deleteuser?id=' + id, this.options).pipe(
            catchError(this.handleError('deleteUser', []))
        );
    }
    login(credentials) {
        return this.http.post<TokenResult>(this.API_BASE_URL + this.BasePath + '/tokenlogin', credentials, this.options).pipe(
            catchError(this.handleError('login', null)));
    }
    linkExternal(credentials): Observable<boolean>  {
        return this.http.post<boolean>(this.API_BASE_URL + this.BasePath + '/linkexternal', credentials, this.options).pipe(
            catchError(this.handleError('linkexternal', null)));
    }
    refreshToken() {
        // Der Token geht im Body, nicht in der URL: bei GACentral-Anmeldung reichen wir
        // dessen Token durch, und ein darin enthaltenes '/' oder '+' zerlegt die URL
        // beziehungsweise wird als Leerzeichen dekodiert.
        return this.http.post<TokenResult>(this.API_BASE_URL + this.BasePath + '/refreshtoken',
            { refreshToken: localStorage.getItem('refresh_token') }, this.options);
    }
    GetCurrentUser(): Observable<any> {
        return this.http.get<any>(this.API_BASE_URL + this.BasePath + '/GetCurrentUser', this.options);
    }
    ResetMFA(id): Observable<any> {
        return this.http.get<any>(this.API_BASE_URL + this.BasePath + '/ResetMFA?id=' + id, this.options);
    }    

    logout() {
        UsersService.LogoutStatic();
    }
    executeLogin(credentials) {
        const promise = new Promise<LoginError>((resolve, reject) => {
            this.login(credentials).subscribe((res: TokenResult) => {
                if (res) {
                    if (res.error) {
                        resolve(res.error_type);
                    } else {
                        this.setTokenResult(res).then(() => {
                            resolve(LoginError.None);
                        });
                    }
                } else {
                    resolve(LoginError.Password);
                }
            });
        });
        return promise;
    }
    executeLinkExternal(credentials) {
        const promise = new Promise<boolean>((resolve, reject) => {
            this.linkExternal(credentials).subscribe((res: boolean) => {
                resolve(res);
            });
        });
        return promise;
    }

    setTokenResult(res) {
        const promise = new Promise((resolve, reject) => {
            if (res) {
                localStorage.setItem('refresh_token', res.refresh_token);
                UsersService.SetToken(res);
                this.GetCurrentUser().subscribe(user => {
                    localStorage.setItem('user', JSON.stringify(user));
                    UsersService.ActualUserRights.next({
                        IsAdmin: user.IsAdministrator === true,
                        Rights: user.Rights
                    });
                    UsersService.Login.next(user);
                    UsersService.ActiveUser.next(user);
                    resolve(user);
                });
            } else {
                UsersService.ActualUserRights.next(null);
                UsersService.Login.next(null);
                UsersService.ActiveUser.next(null);
                resolve(null);
            }
        });
        return promise;
    }

    initUserFromLocalStorage() {
        const userJson = localStorage.getItem('user');
        if (userJson) {
            const user = JSON.parse(userJson);
            UsersService.ActualUserRights.next({
                IsAdmin: user.IsAdministrator === true,
                Rights: user.Rights
            });
            UsersService.ActiveUser.next(user);
            return user;
        }
        return null;
    }

    executeAnonymousLogin(anonymousUser) {
        const p = this.API_BASE_URL + this.BasePath + '/TokenLoginAnonymous?userID=' + anonymousUser;
        return this.http.get<TokenResult>(p, this.options).pipe(catchError(this.handleError('anonymousLogin', null))).subscribe(res => {
            this.setTokenResult(res).then(() => { });
        });
    }
    ExternalLogin(provider, apibase, returnUrl) {
        const p = this.API_BASE_URL + this.BasePath + '/ExternalLogin?provider=' + provider + '&apibase=' + apibase + "&returnUrl=" + returnUrl;
        return this.http.get<TokenResult>(p, this.options)
    }
    //ExternalAuthCallback(returnUrl) {
    //    const p = this.API_BASE_URL + this.BasePath + '/ExternalAuthCallback?returnUrl=' + returnUrl;
    //    return this.http.get<TokenResult>(p, this.options);
    //}
    LinkLogin(provider, returnUrl) {
        const p = this.API_BASE_URL + this.BasePath + '/LinkLogin?provider=' + provider + '&apibase=' + this.API_BASE_URL.substring(0, this.API_BASE_URL.length -1) + "&returnUrl=" + returnUrl;
        return this.http.post<TokenResult>(p,null, this.options)
    }
    //LinkLoginCallback(provider, apibase, returnUrl) {
    //    const p = this.API_BASE_URL + this.BasePath + '/LinkLoginCallback?provider=' + provider + '&apibase=' + apibase + "&returnUrl=" + returnUrl;
    //    return this.http.get<TokenResult>(p, this.options).pipe(catchError(this.handleError('anonymousLogin', null))).subscribe(res => {

    //    })
    //}
}
